import { useCallback, useEffect, useState } from 'react';
import localeContract from '../../shared/i18n/contract/locales.json';

export type Locale = 'en-US' | 'zh-CN' | 'zh-TW';

const messages = {
  'en-US': {
    accountSignIn: "Sign in to OpenBitFun",
    githubSignIn: "Continue with GitHub",
    emailLoginIntro: "Use an email code or GitHub. No password needed.",
    authOr: "or",
    authPrivate: "Password-free. Your code is only used to sign in.",
    emailAddress: "Email address",
    emailCode: "Verification code",
    emailSendCode: "Send verification code",
    emailVerify: "Verify and sign in",
    emailCodeSent: "Code sent. Check your inbox and spam folder.",
    emailResend: "Resend code",
    emailResendWait: "Please wait before resending",
    authWorking: "Please wait…",
    emailChange: "Use another email",
    emailInvalid: "Enter a valid email address.",
    emailCodeInvalid: "Incorrect or expired code. Request a new code if needed.",
    emailRateLimited: "Too many requests. Please try again later.",
    emailFlowExpired: "This sign-in request has expired or already finished. Close this window and start sign-in again from the app or website you were using.",
    emailDeliveryFailed: "We couldn't send the email. Wait a minute, then try again.",
    emailAuthBusy: "Sign-in is busy. Wait a minute, then try again.",
    emailStartFailed: "Sign-in could not be completed. Please try again or reopen the sign-in page.",
    emailUnavailable: "Sign-in is unavailable on this server.",

    market: 'MiniApp Market',
    discover: 'Discover',
    submit: 'Submit',
    submissions: 'My submissions',
    admin: 'Review',
    signIn: 'Sign in',
    signOut: 'Sign out',
    headline: 'Useful MiniApps, right inside OpenBitFun.',
    intro:
      'Discover reviewed tools that respect your permissions and fit the way you already work.',
    search: 'Search MiniApps',
    allCategories: 'All categories',
    newest: 'Newest',
    downloads: 'Most downloaded',
    rating: 'Top rated',
    empty: 'No reviewed MiniApps match this view yet.',
    loadMore: 'Load more',
    install: 'Download .bfminiapp',
    favorite: 'Favorite',
    favorited: 'Favorited',
    by: 'by',
    version: 'Release',
    requires: 'Requires OpenBitFun',
    permissions: 'Requested capabilities',
    changelog: 'What changed',
    releases: 'Release history',
    submitTitle: 'Submit a MiniApp',
    submitIntro:
      'Upload a self-contained .bfminiapp package and 1–5 marketplace images. Node, npm, and remote executable dependencies are rejected.',
    signInRequired: 'Sign in to continue.',
    package: '.bfminiapp package',
    screenshots: 'Marketplace images',
    listingImageHint:
      'A 16:9 image is recommended for the marketplace; the first image becomes the listing cover. Keep the composition clear, the subject prominent, and key content near the center. PNG, JPEG, or WebP; up to 5 MiB each; 1920×1080 recommended.',
    publishForReview: 'Submit for human review',
    uploading: 'Uploading and validating…',
    submitted: 'Submitted for review.',
    mySubmissions: 'My submissions',
    noSubmissions: 'You have not submitted a MiniApp yet.',
    webSubmissionDisabledTitle: 'Web submissions are temporarily unavailable',
    webSubmissionDisabledBody:
      'Web uploads and submission actions are currently disabled. Your existing submission history remains available.',
    submitWithDesktop: 'Submit from OpenBitFun Desktop',
    desktopSubmissionHint:
      'To publish a MiniApp or a new release, use the MiniApp Market in the OpenBitFun Desktop client.',
    reviewQueue: 'Human review queue',
    approve: 'Approve and publish',
    reject: 'Reject',
    rejectionReason: 'Rejection reason',
    source: 'Reviewed source',
    sourceDiff: 'Previous release diff',
    currentSource: 'Current source',
    withdraw: 'Withdraw',
    moderationReason: 'Moderation reason',
    yank: 'Yank release',
    unpublish: 'Unpublish listing',
    back: 'Back to market',
    authComplete: 'Sign-in complete',
    authCompleteBody: 'Return to the app or browser window where you started signing in. You can close this tab.',
    authSharedIdentity: 'Use the same OpenBitFun account for the marketplaces and remote device control.',
    openbitfunHome: 'OpenBitFun home',
    getOpenBitFunTitle: 'New to OpenBitFun?',
    getOpenBitFunCatalogNote:
      'MiniApps run inside the OpenBitFun desktop client. Download it once, then install anything from this market.',
    getOpenBitFunListingNote:
      'This MiniApp runs inside the OpenBitFun desktop client. Download it, then open the .bfminiapp package.',
    getOpenBitFunAction: 'Download OpenBitFun',
    navigationLabel: 'Marketplace navigation',
    language: 'Language',
    changeLanguage: 'Change language',
    switchToLight: 'Use light theme',
    switchToDark: 'Use dark theme',
    heroEyebrow: 'Reviewed for OpenBitFun',
    heroImageAlt: 'Reviewed MiniApp packages inside OpenBitFun',
    marketSafety: 'Marketplace safety',
    trustSource: 'Source reviewed before publishing',
    trustHash: 'Immutable package hashes',
    trustPermissions: 'You approve every permission',
    catalogIntro: 'Reviewed MiniApps you can download and open in OpenBitFun.',
    reviewedApp: 'reviewed app',
    reviewedApps: 'reviewed apps',
    sortLabel: 'Sort MiniApps',
    screenshotLabel: 'marketplace image',
    loading: 'Loading…',
    submitUpdate: 'Submit update',
    ratingLabel: 'Rating',
    stars: 'stars',
    downloadsLabel: 'Downloads',
    favoritesLabel: 'Favorites',
    licenseLabel: 'License',
    customLicense: 'Custom',
    viewSourceRepository: 'View source repository',
    yankedLabel: 'Yanked',
    publisherWorkspace: 'Publisher workspace',
    choosePackageAndScreenshot:
      'Choose one .bfminiapp package and at least one marketplace image.',
    listingSection: 'Listing',
    slugLabel: 'Slug',
    releaseNumberLabel: 'Release number',
    nameLabel: 'Name',
    iconLabel: 'Icon',
    categoryLabel: 'Category',
    tagsLabel: 'Tags',
    tagsPlaceholder: 'git, focus, data',
    descriptionLabel: 'Description',
    releaseSection: 'Release',
    minOpenBitFunVersionLabel: 'Minimum OpenBitFun version',
    publicRepositoryOptional: 'Public repository (optional)',
    licenseTypeLabel: 'License type',
    spdxExpression: 'SPDX expression',
    customLicenseUrl: 'Custom license URL',
    reviewBundle: 'Review bundle',
    beforeUpload: 'Before upload',
    packageSafety: 'The package must be self-contained and explicitly set node.enabled=false.',
    publisherHistory: 'Publisher history',
    adminEyebrow: 'Admin review',
    administratorRequired: 'Administrator access required.',
    queueClear: 'The queue is clear.',
    reviewPlaceholder: 'Select a submission to inspect its immutable review bundle.',
    submitterLabel: 'Submitted by',
    submittedAtLabel: 'Submitted at',
    releaseLabel: 'Release',
    minimumOpenBitFunLabel: 'Minimum OpenBitFun',
    notDeclared: 'Not declared',
    publicRepository: 'Public repository',
    submissionScreenshot: 'Submitted marketplace image',
    packageSha256: 'Package SHA-256',
    noSourceChanges: 'No changes from the previous release.',
    permissionPrivateStorage: 'Private app storage',
    permissionReadFiles: 'Read files',
    permissionWriteFiles: 'Write files',
    permissionRunCommand: 'Run command',
    permissionNetwork: 'Network',
    permissionAi: 'Use configured AI models',
    permissionAgent: 'Start visible OpenBitFun agent runs',
    permissionNotifications: 'Send system notifications',
    permissionNodeUnavailable: 'Node runtime unavailable',
    packagePending: 'package pending',
    statusDraft: 'Draft',
    statusSubmitted: 'Submitted',
    statusApproved: 'Approved',
    statusRejected: 'Rejected',
    statusWithdrawn: 'Withdrawn',
    marketNotPublic: 'The MiniApp marketplace is not open to the public yet.',
    oauthNotConfigured: 'Sign-in is being configured. Please try again shortly.',
    categoryDeveloper: 'Developer',
    categoryProductivity: 'Productivity',
    categoryData: 'Data',
    categoryCreative: 'Creative',
    categoryEducation: 'Education',
    categoryUtilities: 'Utilities',
    categoryEntertainment: 'Entertainment',
    categoryOther: 'Other',
  },
  'zh-CN': {
    accountSignIn: "登录 OpenBitFun",
    githubSignIn: "使用 GitHub 登录",
    emailLoginIntro: "使用邮箱验证码或 GitHub 登录，无需设置密码。",
    authOr: "或",
    authPrivate: "无需密码，验证码仅用于本次登录。",
    emailAddress: "邮箱地址",
    emailCode: "验证码",
    emailSendCode: "发送验证码",
    emailVerify: "验证并登录",
    emailCodeSent: "验证码已发送，请检查收件箱和垃圾邮件。",
    emailResend: "重新发送",
    emailResendWait: "请稍后重新发送",
    authWorking: "请稍候…",
    emailChange: "更换邮箱",
    emailInvalid: "请输入有效的邮箱地址。",
    emailCodeInvalid: "验证码错误或已过期，请重试或重新获取。",
    emailRateLimited: "请求过于频繁，请稍后重试。",
    emailFlowExpired: "本次登录已过期或已完成。请关闭此窗口，返回原来的应用或网站重新发起登录。",
    emailDeliveryFailed: "邮件发送失败，请等待一分钟后重试。",
    emailAuthBusy: "登录服务繁忙，请等待一分钟后重试。",
    emailStartFailed: "暂时无法完成登录，请重试或重新打开登录页面。",
    emailUnavailable: "此服务器尚未配置登录方式。",

    market: 'MiniApp 市场',
    discover: '发现',
    submit: '投稿',
    submissions: '我的投稿',
    admin: '审核',
    signIn: '登录',
    signOut: '退出登录',
    headline: '好用的 MiniApp，就在 OpenBitFun 里。',
    intro: '发现经过审核的小工具，在熟悉的工作流里安全安装、按需授权。',
    search: '搜索 MiniApp',
    allCategories: '全部分类',
    newest: '最新发布',
    downloads: '下载最多',
    rating: '评分最高',
    empty: '暂时没有符合条件的已审核 MiniApp。',
    loadMore: '加载更多',
    install: '下载 .bfminiapp',
    favorite: '收藏',
    favorited: '已收藏',
    by: '作者',
    version: '版本',
    requires: '需要 OpenBitFun',
    permissions: '申请的能力',
    changelog: '更新说明',
    releases: '版本历史',
    submitTitle: '投稿 MiniApp',
    submitIntro: '上传自包含的 .bfminiapp 和 1–5 张市场展示图。Node、npm 和远程可执行依赖会被拒绝。',
    signInRequired: '请先登录。',
    package: '.bfminiapp 安装包',
    screenshots: '市场展示图',
    listingImageHint:
      '市场展示图建议使用 16:9；第一张会作为列表封面。建议画面清晰、主体突出，并将关键信息放在画面中部。支持 PNG、JPEG 或 WebP，每张不超过 5 MiB，推荐 1920×1080。',
    publishForReview: '提交人工审核',
    uploading: '正在上传并校验…',
    submitted: '已提交审核。',
    mySubmissions: '我的投稿',
    noSubmissions: '你还没有投稿。',
    webSubmissionDisabledTitle: '网页投稿暂未开放',
    webSubmissionDisabledBody: '网页端的上传和投稿操作目前已关闭，已有投稿记录仍可继续查看。',
    submitWithDesktop: '请在 OpenBitFun Desktop 中投稿',
    desktopSubmissionHint:
      '如需发布 MiniApp 或提交新版本，请前往 OpenBitFun Desktop 客户端中的 MiniApp 市场操作。',
    reviewQueue: '人工审核队列',
    approve: '批准并发布',
    reject: '驳回',
    rejectionReason: '驳回原因',
    source: '待审源码',
    sourceDiff: '与上一版本差异',
    currentSource: '当前源码',
    withdraw: '撤回',
    moderationReason: '下架原因',
    yank: '永久撤下版本',
    unpublish: '下架 Listing',
    back: '返回市场',
    authComplete: '登录完成',
    authCompleteBody: '请返回发起登录的应用或浏览器窗口。你可以关闭此标签页。',
    authSharedIdentity: '市场与远程设备控制共用你的 OpenBitFun 账号。',
    openbitfunHome: 'OpenBitFun 官网',
    getOpenBitFunTitle: '没有 OpenBitFun？',
    getOpenBitFunCatalogNote:
      'MiniApp 需要在 OpenBitFun 客户端里运行。下载客户端后，就能安装市场里的任意 MiniApp。',
    getOpenBitFunListingNote:
      '这个 MiniApp 需要在 OpenBitFun 客户端里运行。下载客户端后，打开 .bfminiapp 安装包即可。',
    getOpenBitFunAction: '下载 OpenBitFun 客户端',
    navigationLabel: '市场导航',
    language: '语言',
    changeLanguage: '切换语言',
    switchToLight: '切换到浅色模式',
    switchToDark: '切换到深色模式',
    heroEyebrow: '为 OpenBitFun 审核发布',
    heroImageAlt: '在 OpenBitFun 中经过审核的 MiniApp 安装包',
    marketSafety: '市场安全机制',
    trustSource: '发布前人工查看源码',
    trustHash: '安装包哈希不可变',
    trustPermissions: '每项权限由你确认',
    catalogIntro: '发现可下载并在 OpenBitFun 中直接打开的已审核 MiniApp。',
    reviewedApp: '个已审核应用',
    reviewedApps: '个已审核应用',
    sortLabel: 'MiniApp 排序',
    screenshotLabel: '市场展示图',
    loading: '正在加载…',
    submitUpdate: '提交新版本',
    ratingLabel: '评分',
    stars: '星',
    downloadsLabel: '下载量',
    favoritesLabel: '收藏数',
    licenseLabel: '许可证',
    customLicense: '自定义',
    viewSourceRepository: '查看源码仓库',
    yankedLabel: '已永久撤下',
    publisherWorkspace: '投稿工作台',
    choosePackageAndScreenshot: '请选择一个 .bfminiapp 安装包和至少一张市场展示图。',
    listingSection: '上架信息',
    slugLabel: '固定 Slug',
    releaseNumberLabel: '市场版本号',
    nameLabel: '名称',
    iconLabel: '图标',
    categoryLabel: '分类',
    tagsLabel: '标签',
    tagsPlaceholder: '开发, 效率, 数据',
    descriptionLabel: '描述',
    releaseSection: '版本信息',
    minOpenBitFunVersionLabel: '最低 OpenBitFun 版本',
    publicRepositoryOptional: '公开源码仓库（可选）',
    licenseTypeLabel: '许可证类型',
    spdxExpression: 'SPDX 表达式',
    customLicenseUrl: '自定义许可证 URL',
    reviewBundle: '审核材料',
    beforeUpload: '上传前确认',
    packageSafety: '安装包必须自包含，并明确设置 node.enabled=false。',
    publisherHistory: '投稿记录',
    adminEyebrow: '管理员人工审核',
    administratorRequired: '需要管理员权限。',
    queueClear: '审核队列为空。',
    reviewPlaceholder: '选择一条投稿，检查其不可变审核材料。',
    submitterLabel: '提交人',
    submittedAtLabel: '提交时间',
    releaseLabel: '市场版本',
    minimumOpenBitFunLabel: '最低 OpenBitFun',
    notDeclared: '未声明',
    publicRepository: '公开源码仓库',
    submissionScreenshot: '投稿展示图',
    packageSha256: '安装包 SHA-256',
    noSourceChanges: '与上一版本没有源码差异。',
    permissionPrivateStorage: '应用私有存储',
    permissionReadFiles: '读取文件',
    permissionWriteFiles: '写入文件',
    permissionRunCommand: '运行命令',
    permissionNetwork: '联网域名',
    permissionAi: '使用已配置的 AI 模型',
    permissionAgent: '启动可见的 OpenBitFun 智能体会话',
    permissionNotifications: '发送系统通知',
    permissionNodeUnavailable: '不可使用 Node 运行时',
    packagePending: '等待上传安装包',
    statusDraft: '草稿',
    statusSubmitted: '待审核',
    statusApproved: '已批准',
    statusRejected: '已驳回',
    statusWithdrawn: '已撤回',
    marketNotPublic: 'MiniApp 市场尚未对公众开放。',
    oauthNotConfigured: '登录正在配置，请稍后再试。',
    categoryDeveloper: '开发工具',
    categoryProductivity: '效率',
    categoryData: '数据',
    categoryCreative: '创作',
    categoryEducation: '教育',
    categoryUtilities: '实用工具',
    categoryEntertainment: '娱乐',
    categoryOther: '其他',
  },
  'zh-TW': {
    accountSignIn: "登入 OpenBitFun",
    githubSignIn: "使用 GitHub 登入",
    emailLoginIntro: "使用信箱驗證碼或 GitHub 登入，無需設定密碼。",
    authOr: "或",
    authPrivate: "無需密碼，驗證碼僅用於本次登入。",
    emailAddress: "電子郵件地址",
    emailCode: "驗證碼",
    emailSendCode: "傳送驗證碼",
    emailVerify: "驗證並登入",
    emailCodeSent: "驗證碼已傳送，請檢查收件匣和垃圾郵件。",
    emailResend: "重新傳送",
    emailResendWait: "請稍後重新傳送",
    authWorking: "請稍候…",
    emailChange: "更換信箱",
    emailInvalid: "請輸入有效的電子郵件地址。",
    emailCodeInvalid: "驗證碼錯誤或已過期，請重試或重新取得。",
    emailRateLimited: "請求過於頻繁，請稍後重試。",
    emailFlowExpired: "本次登入已過期或已完成。請關閉此視窗，返回原來的應用程式或網站重新發起登入。",
    emailDeliveryFailed: "郵件傳送失敗，請等待一分鐘後重試。",
    emailAuthBusy: "登入服務忙碌，請等待一分鐘後重試。",
    emailStartFailed: "暫時無法完成登入，請重試或重新開啟登入頁面。",
    emailUnavailable: "此伺服器尚未設定登入方式。",

    market: 'MiniApp 市場',
    discover: '探索',
    submit: '投稿',
    submissions: '我的投稿',
    admin: '審核',
    signIn: '登入',
    signOut: '登出',
    headline: '好用的 MiniApp，就在 OpenBitFun 裡。',
    intro: '探索經過審核的小工具，在熟悉的工作流程裡安全安裝、按需授權。',
    search: '搜尋 MiniApp',
    allCategories: '全部分類',
    newest: '最新發布',
    downloads: '下載最多',
    rating: '評分最高',
    empty: '暫時沒有符合條件的已審核 MiniApp。',
    loadMore: '載入更多',
    install: '下載 .bfminiapp',
    favorite: '收藏',
    favorited: '已收藏',
    by: '作者',
    version: '版本',
    requires: '需要 OpenBitFun',
    permissions: '申請的能力',
    changelog: '更新說明',
    releases: '版本歷史',
    submitTitle: '投稿 MiniApp',
    submitIntro: '上傳自包含的 .bfminiapp 和 1–5 張市場展示圖。Node、npm 和遠端可執行依賴會被拒絕。',
    signInRequired: '請先登入。',
    package: '.bfminiapp 安裝包',
    screenshots: '市場展示圖',
    listingImageHint:
      '市場展示圖建議使用 16:9；第一張會作為列表封面。建議畫面清晰、主體突出，並將關鍵資訊放在畫面中部。支援 PNG、JPEG 或 WebP，每張不超過 5 MiB，建議 1920×1080。',
    publishForReview: '提交人工審核',
    uploading: '正在上傳並驗證…',
    submitted: '已提交審核。',
    mySubmissions: '我的投稿',
    noSubmissions: '你還沒有投稿。',
    webSubmissionDisabledTitle: '網頁投稿暫未開放',
    webSubmissionDisabledBody: '網頁端的上傳和投稿操作目前已關閉，既有投稿記錄仍可繼續查看。',
    submitWithDesktop: '請在 OpenBitFun Desktop 中投稿',
    desktopSubmissionHint:
      '如需發布 MiniApp 或提交新版本，請前往 OpenBitFun Desktop 客戶端中的 MiniApp 市場操作。',
    reviewQueue: '人工審核佇列',
    approve: '核准並發布',
    reject: '駁回',
    rejectionReason: '駁回原因',
    source: '待審原始碼',
    sourceDiff: '與上一版本差異',
    currentSource: '目前原始碼',
    withdraw: '撤回',
    moderationReason: '下架原因',
    yank: '永久撤下版本',
    unpublish: '下架 Listing',
    back: '返回市場',
    authComplete: '登入完成',
    authCompleteBody: '請返回發起登入的應用程式或瀏覽器視窗。你可以關閉此分頁。',
    authSharedIdentity: '市場與遠端裝置控制共用你的 OpenBitFun 帳號。',
    openbitfunHome: 'OpenBitFun 官網',
    getOpenBitFunTitle: '還沒有 OpenBitFun？',
    getOpenBitFunCatalogNote:
      'MiniApp 需要在 OpenBitFun 客戶端裡執行。下載客戶端後，就能安裝市場裡的任意 MiniApp。',
    getOpenBitFunListingNote:
      '這個 MiniApp 需要在 OpenBitFun 客戶端裡執行。下載客戶端後，開啟 .bfminiapp 安裝包即可。',
    getOpenBitFunAction: '下載 OpenBitFun 客戶端',
    navigationLabel: '市場導覽',
    language: '語言',
    changeLanguage: '切換語言',
    switchToLight: '切換到淺色模式',
    switchToDark: '切換到深色模式',
    heroEyebrow: '為 OpenBitFun 審核發布',
    heroImageAlt: '在 OpenBitFun 中經過審核的 MiniApp 安裝包',
    marketSafety: '市場安全機制',
    trustSource: '發布前人工查看原始碼',
    trustHash: '安裝包雜湊不可變',
    trustPermissions: '每項權限由你確認',
    catalogIntro: '探索可下載並在 OpenBitFun 中直接開啟的已審核 MiniApp。',
    reviewedApp: '個已審核應用程式',
    reviewedApps: '個已審核應用程式',
    sortLabel: 'MiniApp 排序',
    screenshotLabel: '市場展示圖',
    loading: '正在載入…',
    submitUpdate: '提交新版本',
    ratingLabel: '評分',
    stars: '星',
    downloadsLabel: '下載量',
    favoritesLabel: '收藏數',
    licenseLabel: '授權條款',
    customLicense: '自訂',
    viewSourceRepository: '查看原始碼倉庫',
    yankedLabel: '已永久撤下',
    publisherWorkspace: '投稿工作臺',
    choosePackageAndScreenshot: '請選擇一個 .bfminiapp 安裝包和至少一張市場展示圖。',
    listingSection: '上架資訊',
    slugLabel: '固定 Slug',
    releaseNumberLabel: '市場版本號',
    nameLabel: '名稱',
    iconLabel: '圖示',
    categoryLabel: '分類',
    tagsLabel: '標籤',
    tagsPlaceholder: '開發, 效率, 資料',
    descriptionLabel: '描述',
    releaseSection: '版本資訊',
    minOpenBitFunVersionLabel: '最低 OpenBitFun 版本',
    publicRepositoryOptional: '公開原始碼倉庫（可選）',
    licenseTypeLabel: '授權類型',
    spdxExpression: 'SPDX 表達式',
    customLicenseUrl: '自訂授權 URL',
    reviewBundle: '審核材料',
    beforeUpload: '上傳前確認',
    packageSafety: '安裝包必須自包含，並明確設定 node.enabled=false。',
    publisherHistory: '投稿記錄',
    adminEyebrow: '管理員人工審核',
    administratorRequired: '需要管理員權限。',
    queueClear: '審核佇列為空。',
    reviewPlaceholder: '選擇一筆投稿，檢查其不可變審核材料。',
    submitterLabel: '投稿人',
    submittedAtLabel: '投稿時間',
    releaseLabel: '市場版本',
    minimumOpenBitFunLabel: '最低 OpenBitFun',
    notDeclared: '未宣告',
    publicRepository: '公開原始碼倉庫',
    submissionScreenshot: '投稿展示圖',
    packageSha256: '安裝包 SHA-256',
    noSourceChanges: '與上一版本沒有原始碼差異。',
    permissionPrivateStorage: '應用程式私有儲存',
    permissionReadFiles: '讀取檔案',
    permissionWriteFiles: '寫入檔案',
    permissionRunCommand: '執行命令',
    permissionNetwork: '連網網域',
    permissionAi: '使用已設定的 AI 模型',
    permissionAgent: '啟動可見的 OpenBitFun 智能體工作階段',
    permissionNotifications: '傳送系統通知',
    permissionNodeUnavailable: '不可使用 Node 執行環境',
    packagePending: '等待上傳安裝包',
    statusDraft: '草稿',
    statusSubmitted: '待審核',
    statusApproved: '已核准',
    statusRejected: '已駁回',
    statusWithdrawn: '已撤回',
    marketNotPublic: 'MiniApp 市場尚未對公眾開放。',
    oauthNotConfigured: '登入正在設定，請稍後再試。',
    categoryDeveloper: '開發工具',
    categoryProductivity: '效率',
    categoryData: '資料',
    categoryCreative: '創作',
    categoryEducation: '教育',
    categoryUtilities: '實用工具',
    categoryEntertainment: '娛樂',
    categoryOther: '其他',
  },
} as const;

export type MessageKey = keyof (typeof messages)['en-US'];

export function resolveMarketLocale(value: string | null): Locale | undefined {
  const normalized = value?.trim().replace(/_/g, '-').toLowerCase();
  if (!normalized) return undefined;
  const aliases = localeContract.locales.flatMap(locale =>
    locale.aliases.map(alias => ({ alias: alias.toLowerCase(), locale: locale.id as Locale })),
  ).sort((left, right) => right.alias.length - left.alias.length);
  return aliases.find(({ alias }) => normalized === alias || normalized.startsWith(`${alias}-`))?.locale;
}

export function initialLocale(): Locale {
  const requested = resolveMarketLocale(new URLSearchParams(window.location.search).get('locale'));
  if (requested) return requested;
  let stored: string | null = null;
  try { stored = localStorage.getItem('openbitfun-market-locale'); } catch { /* Storage can be blocked in an auth popup. */ }
  return resolveMarketLocale(stored) ?? resolveMarketLocale(navigator.language) ?? localeContract.fallbackLocale as Locale;
}

export function useLocale() {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const t = useCallback((key: MessageKey) => messages[locale][key], [locale]);
  useEffect(() => {
    document.documentElement.lang = locale;
    try { localStorage.setItem('openbitfun-market-locale', locale); } catch { /* Keep the selected language in memory. */ }
  }, [locale]);
  return {
    locale,
    setLocale: (next: Locale) => {
      const url = new URL(window.location.href);
      url.searchParams.set('locale', next);
      window.history.replaceState(null, '', url);
      setLocaleState(next);
    },
    t,
  };
}
